import {
  HISTORY_VALUE_KIND,
  isOrderStatus,
  ORDER_HISTORY_EVENT_CODE,
  ORDER_STATUS,
  type HistoryEntry,
  type HistoryStatusValue,
  type OrderStatus,
} from '@klareospace/pharos-lib-types';
import type { LifecycleConnector, LifecycleStep } from '@klareospace/pharos-lib-web-utils';

// ─── Constants ────────────────────────────────────────────────────────────────

/**
 * The canonical forward-progress status chain (ordered).
 * Terminal statuses (completed / failed / cancelled) are handled separately
 * as the final slot — they are NOT in the chain.
 * Off-chain non-terminal statuses (pending / draft / carrier_rejected) are
 * anchored to the last chain status present in history for windowing.
 */
export const LIFECYCLE_CHAIN: OrderStatus[] = [
  ORDER_STATUS.NEW,
  ORDER_STATUS.CARRIER_ASSIGNED,
  ORDER_STATUS.DRIVER_ASSIGNED,
  ORDER_STATUS.CARRIER_ACCEPTED,
  ORDER_STATUS.DRIVER_ACCEPTED,
  ORDER_STATUS.DRIVING_TO_PICKUP,
  ORDER_STATUS.ARRIVED_AT_PICKUP,
  ORDER_STATUS.WAITING_FOR_PICKUP,
  ORDER_STATUS.PICKUP_COMPLETED,
  ORDER_STATUS.IN_TRANSIT,
  ORDER_STATUS.DRIVING_TO_DELIVERY,
  ORDER_STATUS.ARRIVED_AT_DELIVERY,
];

const TERMINAL_FINAL_STATUSES: OrderStatus[] = [ORDER_STATUS.COMPLETED, ORDER_STATUS.FAILED, ORDER_STATUS.CANCELLED];

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LifecycleSlot {
  /** Matches t(`status.${status}`) in the caller */
  label: OrderStatus;
  /**
   * null for upcoming steps; raw ISO timestamp string for done/active steps.
   * Callers should format this for display (e.g. via formatEventTimestamp).
   */
  timestamp: string | null;
  /** Visual state */
  state: 'done' | 'active' | 'upcoming';
  /** Optional reason (e.g. failure or rejection reason) */
  reason: string | undefined;
  /** Optional connector info — how many steps collapsed before this slot */
  connector: LifecycleConnector | undefined;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isFinalStatus(status: OrderStatus): boolean {
  return TERMINAL_FINAL_STATUSES.includes(status);
}

/** The `status`-value fields this mapper needs, keyed by status for O(1) lookups. */
interface StatusHistoryRecord {
  reason: string | undefined;
  timestamp: string;
}

function getStatusValue(entry: HistoryEntry): HistoryStatusValue | null {
  return entry.newValue !== null && entry.newValue.kind === HISTORY_VALUE_KIND.STATUS ? entry.newValue : null;
}

/**
 * Build a Map from `status`-coded history entries, plus the `created` sentinel when it carries a
 * status value (the first stable status, since the PHA-690 curation). `history` arrives
 * most-recent-first (the backend contract), so the first occurrence of a given status is already
 * its most recent one — later (older) duplicates, including the `created` sentinel itself, are
 * skipped. This replaces repeated array scans with O(1) lookups.
 */
function buildHistoryMap(history: HistoryEntry[]): Map<OrderStatus, StatusHistoryRecord> {
  const map = new Map<OrderStatus, StatusHistoryRecord>();
  for (const entry of history) {
    if (entry.code !== ORDER_HISTORY_EVENT_CODE.STATUS && entry.code !== ORDER_HISTORY_EVENT_CODE.CREATED) continue;
    const value = getStatusValue(entry);
    if (value === null || !isOrderStatus(value.status)) continue;
    if (map.has(value.status)) continue;
    map.set(value.status, { reason: value.reason, timestamp: entry.timestamp });
  }
  return map;
}

function getTimestampForStatus(status: OrderStatus, historyMap: Map<OrderStatus, StatusHistoryRecord>): string | null {
  return historyMap.get(status)?.timestamp ?? null;
}

function getReasonForStatus(
  status: OrderStatus,
  historyMap: Map<OrderStatus, StatusHistoryRecord>,
): string | undefined {
  return historyMap.get(status)?.reason;
}

function isStatusInHistory(status: OrderStatus, historyMap: Map<OrderStatus, StatusHistoryRecord>): boolean {
  return historyMap.has(status);
}

/**
 * Resolves the anchor chain index used for window centering.
 *
 * - In-chain status: its own index.
 * - Terminal status: chainLength (past end, window shifts left to show last 3).
 * - Off-chain non-terminal (PENDING, DRAFT, CARRIER_REJECTED, ANOMALY):
 *   last chain status present in history, fallback to 0 (NEW).
 */
function resolveAnchorChainIndex(
  currentStatus: OrderStatus,
  historyMap: Map<OrderStatus, StatusHistoryRecord>,
): number {
  const inChainIdx = LIFECYCLE_CHAIN.indexOf(currentStatus);
  if (inChainIdx !== -1) return inChainIdx;
  if (isFinalStatus(currentStatus)) return LIFECYCLE_CHAIN.length;

  // Off-chain non-terminal: anchor on deepest chain step found in history
  for (let i = LIFECYCLE_CHAIN.length - 1; i >= 0; i--) {
    if (isStatusInHistory(LIFECYCLE_CHAIN[i], historyMap)) return i;
  }
  return 0;
}

/**
 * Computes the [start, end] window indices into middlePool given the centred index.
 * Always returns a window of exactly 3 items (clamped to pool bounds).
 */
function computeWindow(midActiveInPool: number, poolLength: number): readonly [number, number] {
  const rawStart = midActiveInPool - 1;
  const rawEnd = midActiveInPool + 1;
  if (rawStart < 0) return [0, Math.min(2, poolLength - 1)];
  if (rawEnd >= poolLength) {
    const end = poolLength - 1;
    return [Math.max(0, end - 2), end];
  }
  return [rawStart, rawEnd];
}

// ─── Core windowing logic ──────────────────────────────────────────────────────

/**
 * Maps history + current status to exactly 5 `LifecycleSlot` slots.
 *
 * Slot layout:
 * - Slot 0 (first): always `New`
 * - Slot 4 (last): the terminal status when terminal, otherwise `Completed`
 * - Slots 1–3 (middle): a 3-step window centred on the anchor step
 *
 * State rules:
 * - In-chain status: 'active' when it is the currentStatus; 'done'/'upcoming'
 *   by position relative to the active step.
 * - Terminal status: 'active' for the terminal slot; chain steps are 'done' if
 *   they appear in history, otherwise 'upcoming' (history-based, not position).
 * - Off-chain non-terminal (PENDING / DRAFT / CARRIER_REJECTED):
 *   no 'active' step; chain steps are 'done' if in history, 'upcoming' otherwise.
 */
export function mapLifecycleSlots(currentStatus: OrderStatus, history: HistoryEntry[]): LifecycleSlot[] {
  // Build a single O(n) pass Map over the `status`-coded entries — all subsequent lookups are O(1).
  const historyMap = buildHistoryMap(history);

  const anchorChainIndex = resolveAnchorChainIndex(currentStatus, historyMap);

  // ─── Build the 5 status slots ─────────────────────────────────────────────

  const firstStatus = ORDER_STATUS.NEW;
  const lastStatus: OrderStatus = isFinalStatus(currentStatus) ? currentStatus : ORDER_STATUS.COMPLETED;

  // Middle pool: LIFECYCLE_CHAIN indices 1..N-1 (slot 0 = NEW is always shown separately)
  const middlePool = LIFECYCLE_CHAIN.slice(1);
  const poolLength = middlePool.length;

  // Map anchor chain index into pool index, clamped to pool bounds
  const midActiveInPool = Math.min(Math.max(anchorChainIndex - 1, 0), poolLength - 1);
  const [midStart, midEnd] = computeWindow(midActiveInPool, poolLength);

  const middleStatuses: OrderStatus[] = [middlePool[midStart], middlePool[midStart + 1], middlePool[midEnd]];
  const allFiveStatuses: OrderStatus[] = [firstStatus, ...middleStatuses, lastStatus];

  // ─── Determine done/active/upcoming ───────────────────────────────────────

  // Whether current status requires history-based (not position-based) state resolution
  const useHistoryForChain = isFinalStatus(currentStatus) || LIFECYCLE_CHAIN.indexOf(currentStatus) === -1;

  function resolveState(status: OrderStatus): 'done' | 'active' | 'upcoming' {
    // Active slot: only when status IS the currentStatus and it belongs in the timeline
    if (status === currentStatus && (LIFECYCLE_CHAIN.includes(currentStatus) || isFinalStatus(currentStatus))) {
      return 'active';
    }
    // Non-active terminal slot is always upcoming
    if (isFinalStatus(status)) return 'upcoming';
    // For terminal or off-chain currentStatus: history-based
    if (useHistoryForChain) {
      return isStatusInHistory(status, historyMap) ? 'done' : 'upcoming';
    }
    // In-chain currentStatus: position-based
    return LIFECYCLE_CHAIN.indexOf(status) < LIFECYCLE_CHAIN.indexOf(currentStatus) ? 'done' : 'upcoming';
  }

  // ─── Build connector for each slot (collapsed counts) ─────────────────────

  function buildConnector(fromStatus: OrderStatus, toStatus: OrderStatus): LifecycleConnector | undefined {
    const fromIdx = LIFECYCLE_CHAIN.indexOf(fromStatus);
    const toIdx = LIFECYCLE_CHAIN.indexOf(toStatus);

    if (fromIdx === -1 || toIdx === -1) return undefined;

    const gapCount = toIdx - fromIdx - 1;
    if (gapCount <= 0) return undefined;

    // Kind determined by the state of the first collapsed step
    const firstCollapsedStatus = LIFECYCLE_CHAIN[fromIdx + 1];
    const kind = resolveState(firstCollapsedStatus) === 'upcoming' ? 'upcoming' : 'done';

    return { collapsedCount: gapCount, kind };
  }

  // ─── Resolve reason ───────────────────────────────────────────────────────

  const isOffChainNonTerminal = !isFinalStatus(currentStatus) && LIFECYCLE_CHAIN.indexOf(currentStatus) === -1;

  function resolveReason(status: OrderStatus, state: 'done' | 'active' | 'upcoming'): string | undefined {
    // Terminal: reason on the active terminal slot
    if (state === 'active' && isFinalStatus(currentStatus)) return getReasonForStatus(status, historyMap);
    // Off-chain non-terminal (e.g. CARRIER_REJECTED): surface reason on the anchor step
    if (isOffChainNonTerminal && LIFECYCLE_CHAIN.indexOf(status) === anchorChainIndex && state === 'done') {
      return getReasonForStatus(currentStatus, historyMap);
    }
    return undefined;
  }

  // ─── Assemble the 5 LifecycleSlot objects ─────────────────────────────────

  return allFiveStatuses.map((status, i) => {
    const state = resolveState(status);
    const timestamp = state !== 'upcoming' ? getTimestampForStatus(status, historyMap) : null;
    const reason = resolveReason(status, state);

    const connector: LifecycleConnector | undefined =
      i === 0
        ? undefined
        : (buildConnector(allFiveStatuses[i - 1], status) ??
            // Adjacent in chain — plain connector; kind derived from state of "to" step
            { kind: state === 'upcoming' ? 'upcoming' : 'done' });

    return {
      connector,
      label: status,
      reason,
      state,
      timestamp,
    };
  });
}

// ─── Re-export for LifecycleTimeline consumption ──────────────────────────────

/**
 * Transforms raw LifecycleSlot[] into LifecycleStep[] ready for LifecycleTimeline.
 *
 * @param slots - output of mapLifecycleSlots
 * @param translateStatus - maps an OrderStatus key to a display label
 * @param formatTimestamp - formats a raw ISO timestamp string for display
 * @param formatConnectorLabel - optional: resolves the pre-computed tooltip label on collapsed connectors
 */
export function toLifecycleSteps(
  slots: LifecycleSlot[],
  translateStatus: (status: OrderStatus) => string,
  formatTimestamp: (iso: string) => string,
  formatConnectorLabel?: (count: number, kind: 'done' | 'upcoming') => string,
): LifecycleStep[] {
  return slots.map((slot) => {
    const connector =
      slot.connector === undefined
        ? undefined
        : {
            ...slot.connector,
            label:
              slot.connector.collapsedCount !== undefined && formatConnectorLabel !== undefined
                ? formatConnectorLabel(slot.connector.collapsedCount, slot.connector.kind)
                : slot.connector.label,
          };

    return {
      connector,
      label: translateStatus(slot.label),
      reason: slot.reason,
      state: slot.state,
      timestamp: slot.timestamp !== null ? formatTimestamp(slot.timestamp) : undefined,
    };
  });
}
